import { Select } from '@/src/components/common/SearchInput/Select'
import useProductInventory from '@/src/hooks/useProductInventory'
import { useDispensing, useProducts, useProfile } from '@hook'
import { yupResolver } from '@hookform/resolvers/yup'
import { DateInputField, FormButton, FormInputField, Loader, Modal, SelectInputField, TextAreaField } from '@ui'
import { capitalizeDrugName, getError, medicationDurationDays, showToast } from '@util'
import { useEffect, useMemo } from 'react'
import { Controller, FormProvider, useForm } from 'react-hook-form'
import { InferType, object, string } from 'yup'

type Props = {
  onClose: () => void
  id?: string
  drugName?: string
  frequency?: string
  startDate?: string
  endDate?: string
  quantity?: string
  dispenseId?: string
  drugQuery?: string
  product_id?: string
  outpatient?: boolean
}

const schema = object({
  endDate: string(),
  expire_date: string(),
  frequency: string(),
  note: string(),
  product_inventory: string().required('batch number is required'),
  batch_number: string().required('batch number is required'),
  qty_dispensed: string().required('Quantity is required'),
  startDate: string(),
  supply_duration: string()
})

export const PrescribeDis = ({
  onClose,
  drugName,
  frequency,
  drugQuery,
  endDate,
  id = '',
  startDate,
  quantity,
  dispenseId,
  product_id,
  outpatient
}: Props) => {
  const { dispense, singleDispense, dispenseIsLoading, updateDispense } = useDispensing({ id: dispenseId })
  const { data, included: singleInc } = singleDispense ?? {}
  const { attributes, relationships } = data ?? {}
  const { profile } = useProfile()
  const methods = useForm({
    resolver: yupResolver(schema),
    values: {
      endDate,
      frequency:
        frequency ||
        attributes?.medication_details.medication_frequency_code ||
        attributes?.medication_details.custom_frequency.interval,
      note: attributes?.note || '',
      batch_number: relationships?.product_inventory.data?.id || '',
      product_inventory: relationships?.product_inventory.data?.id || '',
      qty_dispensed: quantity || attributes?.qty_dispensed || '',
      startDate,
      supply_duration: attributes?.supply_duration
    }
  })
  const productID = relationships?.product.data?.id || ''

  const { products, included } = useProducts({
    query:
      drugQuery ||
      singleInc?.find(({ type }) => type === 'product')?.attributes?.strength ||
      singleInc?.find(({ type }) => type === 'product')?.attributes?.api_strengths?.find((_, idx) => idx === 0)
        ?.api_name
  })
  const { inventories, includedData } = useProductInventory({
    product_id: product_id || productID,
    is_main_pharmacy: true,
    facility_id: profile?.relationships?.facility?.data?.id || ''
  })
  const { handleSubmit, watch, setValue, formState } = methods
  const product = watch('product_inventory')
  const batchId = watch('batch_number')

  // FIX 1: Map inventories to the correct { label, value } shape for the Select component.
  const batchOptions = useMemo(() => {
    if (product && inventories) {
      return inventories.map(({ attributes: { batch_number, strength, stock_quantity, expiry_date }, id }) => ({
        label: `${batch_number} (${strength}) (${stock_quantity}) ${expiry_date}`,
        value: id
      }))
    }
    return []
  }, [product, inventories])

  // Update the dependency array for the `value` useMemo now that `batchOptions` is defined
  const valueForSelect = useMemo(() => {
    if (batchId) {
      const currentOption = batchOptions.find(opt => opt?.value === batchId)
      return currentOption || { label: '', value: '' }
    } else {
      return { label: '', value: '' }
    }
  }, [batchId, batchOptions])

  const productOptions = useMemo(() => {
    const dosage_form = includedData && includedData[0]?.attributes?.dosage_form
    if (inventories) {
      return inventories?.map(({ attributes: { drug_name, strength, api_strengths }, id }) => ({
        id,
        name: `${dosage_form} ${capitalizeDrugName(drug_name)}  ${strength || api_strengths?.map(({ strength }) => strength).join('/')}`
      }))
    }
    return []
  }, [inventories, includedData])

  useEffect(() => {
    const days = medicationDurationDays(startDate, endDate)
    if (days != null && days > 0) setValue('supply_duration', `${days} ${days === 1 ? 'day' : 'days'}`)
  }, [setValue, startDate, endDate])

  useEffect(() => {
    if (batchId && included) {
      const expireDate = included.find(({ id }) => id === batchId)?.attributes.expiry_date
      setValue('expire_date', expireDate)
    }
  }, [batchId, included, setValue])

  const onSubmit = async ({ product_inventory, qty_dispensed, note, batch_number }: InferType<typeof schema>) => {
    const payload = {
      data: {
        attributes: {
          dispensary_unit: 'ICU',
          packaging: 'bag',
          qty_dispensed,
          ...(note && { note }),
          dispensary_type: outpatient ? 'outpatient_DAP' : 'inpatient_DAP'
        },
        relationships: {
          ...(id && {
            medication: {
              data: {
                id,
                type: 'medication'
              }
            }
          }),
          product: {
            data: {
              id: product_id || productID,
              type: 'product'
            }
          },
          product_inventory: {
            data: {
              id: product_inventory ?? batch_number, // The ID is correctly submitted here
              type: 'product_inventory'
            }
          }
        },
        type: 'dispensing'
      }
    }
    console.log(payload)

    try {
      dispenseId ? await updateDispense({ id: dispenseId, payload }) : await dispense(payload)
      showToast(dispenseId ? 'Prescription Fill Updated Successfully' : 'Prescription Filled Successfully')
      onClose()
    } catch (error) {
      getError(error)
    }
  }

  product

  return (
    <FormProvider {...methods}>
      <Modal onClose={onClose} title={dispenseId ? 'Edit Filled Prescription' : 'Fill Prescription'}>
        {dispenseIsLoading && <Loader />}
        <form className="flex flex-col overflow-auto w-full max-w-[600px]" onSubmit={handleSubmit(onSubmit)}>
          <div className="flex flex-col overflow-auto p-6 gap-4">
            <SelectInputField
              className="flex flex-col"
              disabled={!!dispenseId}
              label="Medication"
              labelKey={'name'}
              name="product_inventory"
              options={productOptions}
              valueKey={{ key: 'id', reduce: true }}
            />
            <div className="">
              <FormInputField disabled label="Frequency" name="frequency" />
            </div>
            <div className="">
              <FormInputField label="Quantity" name="qty_dispensed" type="number" />
            </div>
            <Controller
              control={methods.control}
              name="batch_number"
              render={({ field }) => (
                <Select
                  borderRadius={'25px'}
                  getOptionLabel={(option: any) => option.label}
                  getOptionValue={(option: any) => option?.value}
                  label="Batch Number"
                  onChange={(val: any) => field.onChange(val?.value)}
                  options={batchOptions}
                  value={valueForSelect}
                />
              )}
            />
            <div className="">
              <DateInputField disabled label="Expire Date" name="expire_date" />
            </div>
            {!dispenseId && (
              <div className="">
                <DateInputField disabled label="Start Date" name="startDate" />
              </div>
            )}
            {!dispenseId && (
              <div className="">
                <DateInputField disabled label="End Date" name="endDate" />
              </div>
            )}
            <div className="">
              <FormInputField disabled label="Supply Duration" name="supply_duration" />
            </div>
            <div>
              <TextAreaField label="Note" name="note" />
            </div>
          </div>
          <FormButton isLoading={formState.isSubmitting} onClose={onClose} submit={dispenseId ? 'Update' : 'Confirm'} />
        </form>
      </Modal>
    </FormProvider>
  )
}
